// Type-only imports: erased at compile time, so they add NO runtime `require` of the
// browser-only SDK. The actual SDK modules are loaded lazily inside activateRumSdks (below)
// via dynamic import(), keeping them out of the server bundle. See activateRumSdks for why.
import type { RumEvent } from '@openobserve/browser-rum';
import type { LogsEvent } from '@openobserve/browser-logs';
import posthog from 'posthog-js';

import { consumeLoginTokenFromUrl, redactUrlToken } from '@restworld/feature-authentication';

import { IUBENDA_PURPOSE, onIubendaConsent } from '../utils/iubenda-consent';
import { trackPageTranslation } from './track-page-translation';

let initialized = false;

/**
 * The slice of the RUM SDK the rest of the app may touch once it is running. The SDK is loaded
 * lazily (see activateRumSdks), so callers go through the helpers below instead of importing it.
 * Nothing orders the SDK start against the page's first effects (RUM starts from `_app` after two
 * dynamic imports, a page can be its own chunk), so what a page says early is held and applied
 * on start rather than dropped: the view name and custom timings.
 */
interface RunningRum {
  startView: (name: string) => void;
  setViewName: (name: string) => void;
  addTiming: (name: string, time?: number) => void;
}

let runningRum: RunningRum | null = null;
// Before the SDK is up the only view is the page-load one, so an early name (from either a
// rename or a new view) has a single slot: the last one wins and lands on that view at start.
let pendingViewName: string | null = null;
// Timings keep the moment they were taken, so applying them late does not move them.
const pendingTimings: Array<{ name: string; time: number }> = [];

/**
 * Start a new RUM view with a name of our choosing. The SDK only opens a view when the path
 * changes, so a flow that moves through `?step=&sub_step=` on one path (/getting-started) is a
 * single view unless it names its own. Errors and API calls can already be told apart without
 * it, since every RUM event carries the URL of the moment; what a view per screen adds is
 * per-screen INP, time spent and action count.
 */
export function startRumView(name: string): void {
  if (runningRum) {
    runningRum.startView(name);
  } else {
    pendingViewName = name;
  }
}

/**
 * Rename the current view without ending it. For the first screen of a flow: starting a new view
 * there would close the page-load view before its LCP is reported, and the load vitals live only
 * on that first view.
 */
export function setRumViewName(name: string): void {
  if (runningRum) {
    runningRum.setViewName(name);
  } else {
    pendingViewName = name;
  }
}

/** Record a named timing on the current view, measured from the view's start. */
export function addRumTiming(name: string): void {
  if (runningRum) {
    runningRum.addTiming(name);
  } else {
    // An epoch timestamp: the SDK measures it from the view's start, as it does for "now".
    pendingTimings.push({ name, time: Date.now() });
  }
}

/**
 * Known-noisy CSP-violation sources. Our CSP is served Report-Only (it blocks
 * nothing), but the browser still fires securitypolicyviolation events for these,
 * flooding RUM: the third-party `fh-*.ecs.*.on.aws` beacon, Google DoubleClick,
 * Cloud Run (`*.run.app`) tags, and our own cross-origin Vercel asset host
 * (`*.vercel.app`, served via assetPrefix). None are our code or a threat.
 *
 * We match on the source URL in the message so a GENUINE, unexpected CSP violation
 * — a real script injection or data exfiltration to an unknown host — still
 * surfaces in RUM instead of being blanket-dropped.
 */
const CSP_NOISE_SOURCE = /\.on\.aws\b|doubleclick\.net|\.run\.app\b|\.vercel\.app\b/i;

/**
 * TEMPORARY noise filter for RUM error tracking. Drops two classes of "error" that
 * dominated the error view with no actionable stack into our code:
 *  - `csp_violation …` from a known-noisy source (see CSP_NOISE_SOURCE) — report-only
 *    third-party / asset noise. Genuine violations from unknown sources are kept.
 *  - `Script error.` — thrown by cross-origin third-party scripts loaded without CORS,
 *    so the browser strips all detail (no message/file/line); inherently unactionable.
 *
 * Stop-gap: revisit once the website CSP is tuned (offending hosts allowlisted or tags
 * removed) and third-party scripts are made CORS-visible. See the O2/RUM cleanup backlog.
 */
function isNoiseError(message: string | undefined): boolean {
  if (!message) return false;
  if (/script error/i.test(message)) return true;
  // CSP: only drop the known-noisy sources — keep genuine/unexpected violations.
  if (message.startsWith('csp_violation')) return CSP_NOISE_SOURCE.test(message);
  return false;
}

/**
 * Take the login token out of every URL an event carries. This is the second defence,
 * not a copy of the address-bar cleanup: the initial document is reported to RUM from
 * the PerformanceNavigationTiming entry, which keeps the URL the page was opened with
 * even after `history.replaceState` has cleaned the address bar. Over the 24 hours to
 * 14/08/2026 that path alone shipped the token in 724 `resource_url` events.
 *
 * Only the fields the SDK lets `beforeSend` modify are touched (see
 * `modifiableFieldPathsByEvent` in browser-rum-core): anything else is dropped silently.
 */
function redactRumEventUrls(event: RumEvent): void {
  if (event.view) {
    event.view.url = redactUrlToken(event.view.url);
    if (event.view.referrer) {
      event.view.referrer = redactUrlToken(event.view.referrer);
    }
  }
  if (event.type === 'resource') {
    event.resource.url = redactUrlToken(event.resource.url);
  }
  if (event.type === 'error') {
    // A URL quoted inside the message or the stack leaves just as surely as one in a field.
    event.error.message = redactUrlToken(event.error.message);
    if (event.error.stack) {
      event.error.stack = redactUrlToken(event.error.stack);
    }
    if (event.error.resource) {
      event.error.resource.url = redactUrlToken(event.error.resource.url);
    }
  }
  if (event.type === 'long_task') {
    // The long-animation-frame entry names what kept the main thread busy, and on the arrival
    // from the e-mail that is the login page itself: `invoker` is the full URL, token included.
    // This is the event the first redaction never looked at, so it went on shipping the
    // credential after the rest had stopped — 278 rows over the 24 hours to 04/09/2026, on the
    // build that carried the fix. `invoker` and `source_url` are the only two fields the SDK
    // lets beforeSend write here (`long_task.scripts[].*` in modifiableFieldPathsByEvent).
    event.long_task.scripts?.forEach((script) => {
      if (script.invoker) {
        script.invoker = redactUrlToken(script.invoker);
      }
      if (script.source_url) {
        script.source_url = redactUrlToken(script.source_url);
      }
    });
  }
}

interface RumSessionReplay {
  startSessionReplayRecording: () => void;
  stopSessionReplayRecording: () => void;
}

/**
 * Record session replays only for users who granted the Iubenda Measurement purpose, the
 * same consent that gates PostHog recordings. A replay is a recording of the screen, which
 * shows names and candidate data, so it must not run for a user who rejected the banner.
 *
 * Two consent sources, as in useMeasurementConsent:
 * - the `iubenda:consent` bridge event: grants and revocations while the page is open, plus
 *   the stored preference Iubenda re-emits on every load;
 * - PostHog's persisted consent, for returning consenters on pages where Iubenda is not
 *   loaded at all (/getting-started). PostHog may finish init after this runs, so it is
 *   re-checked on `onFeatureFlags`, which only fires once init has completed.
 *
 * Start and stop are both safe to repeat: the SDK ignores a start while a recording is in
 * progress and a stop when nothing is recording. Only the replay is gated here; the rest of
 * RUM is unchanged.
 */
function gateSessionReplayOnConsent(rum: RumSessionReplay): void {
  const applyConsent = (granted: boolean) => {
    if (granted) {
      rum.startSessionReplayRecording();
    } else {
      rum.stopSessionReplayRecording();
    }
  };

  const syncFromPostHog = () => {
    if (posthog.__loaded && posthog.get_explicit_consent_status() === 'granted') applyConsent(true);
  };
  syncFromPostHog();
  posthog.onFeatureFlags(syncFromPostHog);

  onIubendaConsent(IUBENDA_PURPOSE.MEASUREMENT, applyConsent);
}

/** Same redaction for the mirrored logs stream, where the URL also reaches `http.url`. */
function redactLogEventUrls(event: LogsEvent): void {
  event.message = redactUrlToken(event.message);
  if (event.view) {
    event.view.url = redactUrlToken(event.view.url);
    if (event.view.referrer) {
      event.view.referrer = redactUrlToken(event.view.referrer);
    }
  }
  if (event.http?.url) {
    event.http.url = redactUrlToken(event.http.url);
  }
  if (event.error?.stack) {
    event.error.stack = redactUrlToken(event.error.stack);
  }
}

/**
 * Initialize OpenObserve RUM (Real User Monitoring) in the browser.
 *
 * Captures, in one SDK shipped to OpenObserve: JS errors/exceptions (symbolicated
 * via uploaded source maps), Core Web Vitals, resource + long-task timing,
 * user-interaction traces, and session replay. Replaces the previous generic
 * OpenTelemetry web-tracing setup that exported to SigNoz.
 *
 * Off by default: a no-op unless `NEXT_PUBLIC_OPENOBSERVE_RUM_CLIENT_TOKEN` and
 * `NEXT_PUBLIC_OPENOBSERVE_RUM_APPLICATION_ID` are set, so it ships dormant and is
 * enabled per-environment via env (Vercel) without a code change. Safe to call
 * multiple times — only the first call wires the SDK.
 *
 * Source-map symbolication matches an error to its map on `service` + `version` +
 * `env`, so those three MUST line up with what the build uploads. On Vercel set
 * `version` to the commit SHA (`NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA`) so each deploy
 * lines up with the maps uploaded for that build.
 *
 * @param service - the calling app's own service name (e.g. `restworld-worker-app`),
 * matching what that app's build uploads maps under so RUM data + symbolication line up
 * per app. `NEXT_PUBLIC_OPENOBSERVE_SERVICE` overrides it when set.
 */
export function initOpenObserveRum({ service: serviceArg }: { service?: string } = {}): void {
  if (initialized || typeof window === 'undefined') {
    return;
  }

  // Belt and braces with the call each app makes while its bundle is evaluated: whichever
  // runs first, RUM never starts on a URL that still carries the login token. Idempotent,
  // so a second call just returns what the first one stashed. Deliberately above the env
  // check below: history and the referrer leak the credential whether RUM is on or not.
  consumeLoginTokenFromUrl();

  const clientToken = process.env.NEXT_PUBLIC_OPENOBSERVE_RUM_CLIENT_TOKEN;
  const applicationId = process.env.NEXT_PUBLIC_OPENOBSERVE_RUM_APPLICATION_ID;
  // Both are required to activate; without them RUM stays off (dormant build).
  if (!clientToken || !applicationId) {
    return;
  }

  initialized = true;

  // Load the browser-only SDKs lazily (dynamic import) so they NEVER enter the server
  // bundle. A static top-level import made Turbopack emit an eager
  // `require('@openobserve/browser-rum')` in the SSR bundle, whose CommonJS core then
  // `require`s the ESM-only `uuid` and crashed every server-rendered page with
  // ERR_REQUIRE_ESM. Guarded by the `typeof window` check above, this runs only in the browser.
  void activateRumSdks({ clientToken, applicationId, service: serviceArg }).catch(() => {
    // Telemetry must never break the app: if an SDK chunk fails to load, skip RUM.
  });
}

/**
 * Second half of {@link initOpenObserveRum}, split out so the browser-only OpenObserve
 * SDKs load via dynamic `import()` (a browser-only chunk) instead of a static import that
 * would land them in the server bundle and crash SSR. Only ever called from the browser.
 */
async function activateRumSdks({
  clientToken,
  applicationId,
  service: serviceArg,
}: {
  clientToken: string;
  applicationId: string;
  service?: string;
}): Promise<void> {
  const site = process.env.NEXT_PUBLIC_OPENOBSERVE_SITE ?? 'o2.restworld.it';
  const organizationIdentifier = process.env.NEXT_PUBLIC_OPENOBSERVE_ORG ?? '';
  const service = process.env.NEXT_PUBLIC_OPENOBSERVE_SERVICE ?? serviceArg ?? 'restworld-employer-app';
  const env = process.env.NEXT_PUBLIC_OPENOBSERVE_ENV ?? 'development';
  // `||` (not `??`) so an empty-string env (e.g. unset VERCEL_GIT_COMMIT_SHA on a local build) falls through.
  const version = process.env.NEXT_PUBLIC_OPENOBSERVE_VERSION || process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA || 'dev';
  const apiBaseUrl = process.env.NEXT_PUBLIC_BASE_API_URL;
  // Self-hosted O2 over plain HTTP (e.g. a local docker instance at http://localhost:5080)
  // needs this true, or the SDK posts to https:// and the send silently fails. Prod/preview
  // point at https://o2.restworld.it, so it defaults to false.
  const insecureHTTP = process.env.NEXT_PUBLIC_OPENOBSERVE_INSECURE === 'true';

  const { openobserveRum } = await import('@openobserve/browser-rum');
  const { openobserveLogs } = await import('@openobserve/browser-logs');

  openobserveRum.init({
    applicationId,
    clientToken,
    site,
    organizationIdentifier,
    service,
    env,
    version,
    // Drop report-only CSP violations + cross-origin "Script error." noise so the
    // RUM error view stays actionable. See isNoiseError above (temporary). Whatever
    // survives leaves without the login token in its URLs.
    beforeSend: (event) => {
      if (event.type === 'error' && isNoiseError(event.error.message)) {
        return false;
      }
      redactRumEventUrls(event);
      return true;
    },
    sessionSampleRate: 100,
    // Every consented session is eligible for replay, but the recorder never starts by
    // itself: with a non-zero sample rate the SDK would otherwise start recording on init,
    // before we know the user's choice. gateSessionReplayOnConsent (below) starts it.
    sessionReplaySampleRate: 100,
    startSessionReplayRecordingManually: true,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    apiVersion: 'v1',
    insecureHTTP,
    // Privacy default still applies to any captured interaction data.
    defaultPrivacyLevel: 'mask-user-input',
    // FE -> BE distributed tracing: inject trace headers on calls to our own API so
    // a RUM action links to the core-services backend trace. 'tracecontext' is the
    // W3C `traceparent` our Elixir/OTel backend reads (the only propagator this SDK
    // version's types accept here).
    ...(apiBaseUrl
      ? {
          allowedTracingUrls: [{ match: apiBaseUrl, propagatorTypes: ['tracecontext'] }],
        }
      : {}),
  });

  // Mirror RUM errors into a logs stream too, so they're searchable/alertable on
  // the logs side as well as in RUM error tracking.
  openobserveLogs.init({
    clientToken,
    site,
    organizationIdentifier,
    service,
    env,
    version,
    apiVersion: 'v1',
    insecureHTTP,
    forwardErrorsToLogs: true,
    // Same noise filter and same redaction as RUM: keep the forwarded error logs clean
    // too, and never let one carry the login token.
    beforeSend: (event) => {
      if (isNoiseError(event.message)) {
        return false;
      }
      redactLogEventUrls(event);
      return true;
    },
  });

  gateSessionReplayOnConsent(openobserveRum);

  runningRum = {
    startView: (name) => openobserveRum.startView(name),
    setViewName: (name) => openobserveRum.setViewName(name),
    addTiming: (name, time) => openobserveRum.addTiming(name, time),
  };
  if (pendingViewName) openobserveRum.setViewName(pendingViewName);
  pendingViewName = null;
  pendingTimings.forEach(({ name, time }) => openobserveRum.addTiming(name, time));
  pendingTimings.length = 0;

  // Tags every event with the language the page is being read in, so a translated session is
  // countable rather than only visible when it crashes. Started after init because it writes
  // global context on the running SDK.
  trackPageTranslation(openobserveRum);
}
